import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { usePersistentTableStore } from '@/stores/persistentTableStore';
import { deepClone } from '@/utils/object';
import {
  isOptimisticFavoriteId,
  useCreateFavoriteFilter,
  useDeleteFavoriteFilter,
  useFavoriteFiltersQuery,
} from '@/composables/api/useFavoriteFiltersApi';
import {
  favoriteRuleSignature,
  type FavoriteFilter,
  type FavoriteRuleSnapshot,
} from '@/utils/favoriteFilters';

/**
 * The seam the RuleRow star button and the FavoriteFilterMenu share (SAU-260):
 * the current entity's favorites plus fingerprint matching and star toggling.
 *
 * Toggling is optimistic — the mutations in useFavoriteFiltersApi patch the
 * cached list immediately and roll back with a toast if the API call fails —
 * so starring feels instant despite the network hop. If the backend isn't
 * deployed yet (endpoint 404s), the query lands in its error state (the menu
 * shows error-with-retry) and a star click rolls back with a toast rather
 * than wedging the row.
 *
 * @param entityId Entity scope override; defaults to the persistent-table
 *                 store's key (the same scoping Saved Views use).
 */
export function useFavoriteFilters(entityId?: MaybeRefOrGetter<string>) {
  const store = usePersistentTableStore();
  /**
   * DURABLE WIRE CONTRACT — the resolved entity id is stored VERBATIM as
   * `favorite_filters.entity` in the backend database, forever. It comes from
   * the route-keyed page ids (persistentTableStore.storeKey: 'TrackMatching',
   * 'AudioFileIssue', 'PlaylistSwitching') or the search entity selection
   * ('LiveTrack', 'LocationZonePlayer', …), so renaming a route-level entity
   * id or consolidating entities silently orphans every favorite saved under
   * the old value — this repo has a live precedent (Player /
   * LocationZonePlayerData → LocationZonePlayer, see entityStore.ts). Any
   * rename requires a backend migration rewriting `favorite_filters.entity`.
   * The production values are pinned by favoriteFilterEntityContract.test.ts.
   */
  const resolvedEntityId = computed(() => toValue(entityId) ?? store.storeKey);

  const query = useFavoriteFiltersQuery(resolvedEntityId);
  const createMutation = useCreateFavoriteFilter();
  const deleteMutation = useDeleteFavoriteFilter();

  /**
   * Server order, trusted as-is: the backend sorts by created_at,id (oldest
   * star first), and optimistic rows are appended last — exactly where the
   * server will slot them once persisted.
   */
  const favorites = computed<FavoriteFilter[]>(() => query.data.value ?? []);

  function findFavorite(
    rule: Pick<FavoriteRuleSnapshot, 'column' | 'field' | 'criterion' | 'value'>,
  ): FavoriteFilter | undefined {
    const signature = favoriteRuleSignature(rule);
    // One stringify per call: the stored signature is authoritative (it's what
    // the backend dedupes on); computing from the rule is only the fallback
    // for the deploy window where the API doesn't send `signature` yet.
    return favorites.value.find(
      (favorite) => (favorite.signature ?? favoriteRuleSignature(favorite.rule)) === signature,
    );
  }

  /** Fingerprint match against the fetched list — drives the star's filled/outline state. */
  function isFavorited(
    rule: Pick<FavoriteRuleSnapshot, 'column' | 'field' | 'criterion' | 'value'>,
  ): boolean {
    return findFavorite(rule) !== undefined;
  }

  /**
   * True while the matching favorite is an optimistic row whose create POST
   * hasn't resolved — the star/X render disabled during that window (a
   * DELETE for the placeholder id would 404 and silently lose the un-star).
   */
  function isFavoritePending(
    rule: Pick<FavoriteRuleSnapshot, 'column' | 'field' | 'criterion' | 'value'>,
  ): boolean {
    const favorite = findFavorite(rule);
    return favorite !== undefined && isOptimisticFavoriteId(favorite.id);
  }

  async function toggleFavorite(
    rule: Pick<FavoriteRuleSnapshot, 'column' | 'field' | 'criterion' | 'value'>,
    label: string,
  ): Promise<void> {
    const existing = findFavorite(rule);
    if (existing) {
      await removeFavorite(existing.id);
      return;
    }

    // Deep-clone the value so the stored snapshot can never alias the live,
    // still-editable rule object.
    const snapshot: FavoriteRuleSnapshot = {
      column: rule.column,
      field: rule.field || null,
      criterion: rule.criterion,
      value: deepClone(rule.value ?? null),
    };

    try {
      await createMutation.mutateAsync({
        entity: resolvedEntityId.value,
        label,
        rule: snapshot,
        signature: favoriteRuleSignature(snapshot),
      });
    } catch {
      // Optimistic rollback + toast are handled by the mutation's onError.
    }
  }

  async function removeFavorite(favoriteFilterId: number): Promise<void> {
    if (isOptimisticFavoriteId(favoriteFilterId)) {
      // Not persisted yet — there is no server id to DELETE (the request
      // would 404 and the create's settle would resurrect the row anyway).
      // The star/X render disabled while pending (see isFavoritePending), so
      // this guard only catches programmatic/racing calls; it's a no-op.
      return;
    }
    try {
      await deleteMutation.mutateAsync({
        entity: resolvedEntityId.value,
        favoriteFilterId,
      });
    } catch {
      // Optimistic rollback + toast are handled by the mutation's onError.
    }
  }

  return {
    favorites,
    isFavorited,
    isFavoritePending,
    toggleFavorite,
    removeFavorite,
    isLoading: computed(() => query.isLoading.value),
    isError: computed(() => query.isError.value),
    refetch: () => query.refetch(),
  };
}
